import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';

import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PlayerSoundtracksComponent } from '../../widgets/player-soundtracks/player-soundtracks.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormDataService } from '../../services/form-data.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { FormService } from '../../services/form.service';
import { CurrencyService } from '../../services/currency.service';
import { PlayerConfig } from '../../models/player-config.interface';
import { UserService } from '../../services/user.service';
import { getPlayerConfigs } from '../../configs/player-configs';

interface ServiceItem { icon: SafeHtml; titleKey: string; descKey: string; }
interface ProcessStep { titleKey: string; descKey: string; }
interface VoiceoverItem { icon: SafeHtml; titleKey: string; descKey: string; }
interface ClientItem { img: string; tag: string; titleKey: string; descKey: string; }
interface TestimonialItem { id: string; logo: string; name: string; textKey: string; track: string; audioUrl: string; }
interface PricingCard { title: string; services: { name: string; price: number; details?: string }[]; }
interface Currency { code: 'EUR' | 'USD' | 'CHF' | 'JPY'; symbol: string; label: string; }

@Component({
    selector: 'app-home',
    imports: [RouterLink, PlayerSoundtracksComponent, TranslateModule],
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('playersWrapper') playersWrapper!: ElementRef;
  @ViewChild('pricingWrapper') pricingWrapper!: ElementRef;

  isPageLoading = true;
  PLAYER_CONFIGS: Record<string, PlayerConfig> = {};

  // Data arrays
  services: ServiceItem[] = [];
  processSteps: ProcessStep[] = [];
  voiceoverServices: VoiceoverItem[] = [];
  clients: ClientItem[] = [];
  testimonials: TestimonialItem[] = [];
  pricingCards: PricingCard[] = [];
  currencies: Currency[] = [
    { code: 'EUR', symbol: '€', label: 'EUR' },
    { code: 'USD', symbol: '$', label: 'USD' },
    { code: 'CHF', symbol: 'CHF', label: 'CHF' }
  ];
  currentCurrency = '€';

  // Audio
  isPlayingTestimonial = false;
  currentTestimonial = '';
  testimonialProgress: Record<string, number> = { kia: 0, edlv: 0 };
  private audioElement: HTMLAudioElement | null = null;
  private progressInterval: any;

  constructor(
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private formData: FormDataService,
    private tokenStorage: TokenStorageService,
    private formService: FormService,
    public currencyService: CurrencyService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.PLAYER_CONFIGS = getPlayerConfigs(this.translate);
    this.initData();
    setTimeout(() => this.isPageLoading = false, 200);
  }

  private initData() {
    // Services
    this.services = [
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.5"/></svg>`), titleKey: 'HOME.SOUNDTRACKS', descKey: 'HOME.SOUNDTRACKS_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M12 3v18M8 7v10M4 10v4M16 7v10M20 10v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`), titleKey: 'HOME.JINGLES', descKey: 'HOME.JINGLES_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M2 10s3-3 5-3 5 3 5 3 3-3 5-3 5 3 5 3" stroke="currentColor" stroke-width="1.5"/><path d="M2 14s3 3 5 3 5-3 5-3 3 3 5 3 5-3 5-3" stroke="currentColor" stroke-width="1.5"/></svg>`), titleKey: 'HOME.SOUND_DESIGN', descKey: 'HOME.SOUND_DESIGN_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M12 14a4 4 0 004-4V6a4 4 0 00-8 0v4a4 4 0 004 4z" stroke="currentColor" stroke-width="1.5"/><path d="M19 10v1a7 7 0 01-14 0v-1M12 18v4M8 22h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`), titleKey: 'HOME.VOICE_OVER', descKey: 'HOME.VOICE_OVER_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" stroke="currentColor" stroke-width="1.5"/><path d="M4 22v-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`), titleKey: 'HOME.HYMNS', descKey: 'HOME.HYMNS_DESC' }
    ];

    // Process steps
    this.processSteps = [
      { titleKey: 'HOW_IT_WORKS.STEP1_TITLE', descKey: 'HOW_IT_WORKS.STEP1_DESC' },
      { titleKey: 'HOW_IT_WORKS.STEP2_TITLE', descKey: 'HOW_IT_WORKS.STEP2_DESC' },
      { titleKey: 'HOW_IT_WORKS.STEP3_TITLE', descKey: 'HOW_IT_WORKS.STEP3_DESC' }
    ];

    // Voiceover services
    this.voiceoverServices = [
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`), titleKey: 'VOICEOVER.ADVERTISING', descKey: 'VOICEOVER.ADVERTISING_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" stroke="currentColor" stroke-width="1.5"/></svg>`), titleKey: 'VOICEOVER.DUBBING', descKey: 'VOICEOVER.DUBBING_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M8 21h8M12 17v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`), titleKey: 'VOICEOVER.DOCUMENTARIES', descKey: 'VOICEOVER.DOCUMENTARIES_DESC' },
      { icon: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 24 24" fill="none"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`), titleKey: 'VOICEOVER.STUDIO_QUALITY', descKey: 'VOICEOVER.STUDIO_QUALITY_DESC' }
    ];

    // Clients
    this.clients = [
      { img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=400&q=80', tag: 'CORP', titleKey: 'HOME.COMPANIES', descKey: 'HOME.COMPANIES_DESC' },
      { img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&q=80', tag: 'ORG', titleKey: 'HOME.ORGANIZATIONS', descKey: 'HOME.ORGANIZATIONS_DESC' },
      { img: 'https://images.unsplash.com/photo-1522529599102-193c0d76b5b6?w=400&q=80', tag: 'PERS', titleKey: 'HOME.PERSONALITIES', descKey: 'HOME.PERSONALITIES_DESC' },
      { img: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&q=80', tag: 'GAME', titleKey: 'HOME.GAME_PUBLISHERS', descKey: 'HOME.GAME_PUBLISHERS_DESC' }
    ];

    // Testimonials
    this.testimonials = [
      { id: 'kia', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b6/KIA_logo3.svg/2560px-KIA_logo3.svg.png', name: 'Kia Motors', textKey: 'HOME.KIA_TESTIMONIAL', track: 'Bad Boy (Radio Edit)', audioUrl: 'https://audio-applisound.s3.eu-west-3.amazonaws.com/testimony/Universe+(created+with+the+Kia+instrument).mp3' },
      { id: 'edlv', logo: 'https://media.licdn.com/dms/image/v2/C4E0BAQG04ut-ZW1iAQ/company-logo_200_200/company-logo_200_200/0/1679067636241/entrepreneurs_dans_la_ville_logo?e=2147483647&v=beta&t=8OXTSrsmSIk1Mp-a9knOEJC2YSZT_1zqiaxJ1adPF8o', name: 'EDLV', textKey: 'HOME.EDLV_TESTIMONIAL', track: 'EDLV (Hymne) v2', audioUrl: 'https://audio-applisound.s3.eu-west-3.amazonaws.com/soundtracks_homePage/Jeyslee+-+EDLV+(Hymne)+v2.mp3' }
    ];

    // Pricing cards
    this.pricingCards = [
      { title: 'PRICING.SOUNDTRACKS_CAT', services: [
        { name: 'PRICING.ST_STANDARD', price: 500 },
        { name: 'PRICING.ST_PREMIUM', price: 1200 },
        { name: 'PRICING.ST_EXCLUSIVE', price: 2500 }
      ]},
      { title: 'PRICING.JINGLES_CAT', services: [
        { name: 'PRICING.JG_SHORT', price: 200 },
        { name: 'PRICING.JG_FULL', price: 450 },
        { name: 'PRICING.JG_PACK', price: 800 }
      ]},
      { title: 'PRICING.VOICEOVER_CAT', services: [
        { name: 'PRICING.VO_STANDARD', price: 150 },
        { name: 'PRICING.VO_PREMIUM', price: 350 },
        { name: 'PRICING.VO_RUSH', price: 500 }
      ]},
      { title: 'PRICING.HYMNS_CAT', services: [
        { name: 'PRICING.HY_STANDARD', price: 800 },
        { name: 'PRICING.HY_PREMIUM', price: 1500 },
        { name: 'PRICING.HY_CUSTOM', price: 3000 }
      ]},
      { title: 'PRICING.SOUND_DESIGN_CAT', services: [
        { name: 'PRICING.SD_BASIC', price: 300 },
        { name: 'PRICING.SD_ADVANCED', price: 700 },
        { name: 'PRICING.SD_FULL', price: 1200 }
      ]}
    ];
  }

  // Scroll to section
  scrollToSection(sectionId: string) {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const nav = document.querySelector('.nav-container') as HTMLElement;
    const offset = nav ? nav.offsetHeight + 20 : 80;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }

  // Carousel controls
  scrollCarousel(dir: number) {
    const el = this.playersWrapper?.nativeElement;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  }

  scrollPricing(dir: number) {
    const el = this.pricingWrapper?.nativeElement;
    if (el) el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  }

  // Currency
  setCurrency(code: 'EUR' | 'USD' | 'CHF' | 'JPY') {
    this.currencyService.setCurrentCurrency(code);
    this.currentCurrency = this.currencies.find(c => c.code === code)?.symbol || '€';
  }

  formatPrice(price: number): string {
    return this.currencyService.convertPrice(price);
  }

  // Audio
  playTestimonial(id: string) {
    const t = this.testimonials.find(x => x.id === id);
    if (!t) return;

    if (this.currentTestimonial === id && this.isPlayingTestimonial) {
      this.audioElement?.pause();
      this.isPlayingTestimonial = false;
      this.clearProgress();
      return;
    }

    this.stopAudio();
    this.currentTestimonial = id;
    this.isPlayingTestimonial = true;
    this.audioElement = new Audio(t.audioUrl);
    this.audioElement.addEventListener('ended', () => {
      this.isPlayingTestimonial = false;
      this.currentTestimonial = '';
      this.clearProgress();
    });
    this.audioElement.play();
    this.startProgress(id);
  }

  private startProgress(id: string) {
    this.progressInterval = setInterval(() => {
      if (this.audioElement) {
        this.testimonialProgress[id] = (this.audioElement.currentTime / this.audioElement.duration) * 100 || 0;
      }
    }, 100);
  }

  private clearProgress() {
    if (this.progressInterval) { clearInterval(this.progressInterval); this.progressInterval = null; }
  }

  private stopAudio() {
    if (this.audioElement) { this.audioElement.pause(); this.audioElement.currentTime = 0; }
    this.clearProgress();
    this.isPlayingTestimonial = false;
  }

  // Quote modal
  openQuote() {
    this.prefillIfLoggedIn();
    this.formService.openOrderForm('custom');
  }

  private prefillIfLoggedIn(): void {
    const user: any = (this.tokenStorage as any)?.getUser?.() || null;
    if (!user) return;
    this.userService.getUserProfile().subscribe({
      next: (p: any) => {
        this.formData.updateFormData({
          personalInfo: { nom: p.lastName || '', prenom: p.firstName || '', email: p.email || '', telephone: p.phone || '', entreprise: p.company || '', fonction: p.jobTitle || '' }
        });
      },
      error: () => {}
    });
  }

  ngOnDestroy() {
    this.stopAudio();
  }
}
